import React, { useState, useEffect, useRef } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { encryptionService } from '../services/encryptionService';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Messages = () => {
  const { user, sessionToken } = useUnifiedAuth();
  const { t, isRTL } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversationKeys, setConversationKeys] = useState({}); // Cache encryption keys
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchConversations();
    }
  }, [user]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      if (!sessionToken) {
        console.error('No session token available for messages');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/conversations`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Fetched conversations:', response.data);
      setConversations(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      if (!sessionToken) {
        console.error('No session token available for fetching messages');
        return;
      }

      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Fetched encrypted messages:', response.data.length);
      
      // Decrypt messages before displaying
      const conversation = conversations.find(c => c.id === conversationId);
      if (conversation && user) {
        let conversationKey = conversationKeys[conversationId];
        
        // Generate conversation key if not cached
        if (!conversationKey) {
          try {
            conversationKey = encryptionService.getConversationKey(user.id, conversation);
            setConversationKeys(prev => ({
              ...prev,
              [conversationId]: conversationKey
            }));
          } catch (error) {
            console.error('Error generating conversation key:', error);
            setMessages(response.data); // Show encrypted messages if key generation fails
            return;
          }
        }

        // Decrypt each message
        const decryptedMessages = response.data.map(message => {
          if (encryptionService.isEncrypted(message.message)) {
            try {
              const decryptedContent = encryptionService.decryptMessage(message.message, conversationKey);
              return {
                ...message,
                message: decryptedContent,
                isDecrypted: true
              };
            } catch (error) {
              console.error('Error decrypting message:', error);
              return {
                ...message,
                message: '[🔒 Encrypted message - unable to decrypt]',
                isDecrypted: false
              };
            }
          }
          return {
            ...message,
            isDecrypted: true // Plain text message
          };
        });

        setMessages(decryptedMessages);
      } else {
        setMessages(response.data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (limit to 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only images (JPEG, PNG, GIF), PDF, and text files are allowed');
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;
    if (!selectedConversation || sending) return;

    if (!sessionToken) {
      console.error('No session token available for sending messages');
      return;
    }

    setSending(true);
    try {
      // Get or generate conversation key
      let conversationKey = conversationKeys[selectedConversation.id];
      if (!conversationKey) {
        try {
          conversationKey = encryptionService.getConversationKey(user.id, selectedConversation);
          setConversationKeys(prev => ({
            ...prev,
            [selectedConversation.id]: conversationKey
          }));
        } catch (error) {
          console.error('Error generating conversation key:', error);
          alert('Unable to encrypt message. Please try again.');
          setSending(false);
          return;
        }
      }

      // Prepare message content
      let messageText = newMessage;
      let attachmentParams = {};

      // Handle file attachment
      if (selectedFile) {
        try {
          // Convert file to base64
          const fileBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]); // Remove data:mime;base64, prefix
            reader.onerror = reject;
            reader.readAsDataURL(selectedFile);
          });

          // Encrypt the file data
          const encryptedFileData = encryptionService.encryptMessage(fileBase64, conversationKey);
          
          attachmentParams = {
            attachment_filename: selectedFile.name,
            attachment_data: encryptedFileData,
            attachment_type: selectedFile.type.startsWith('image/') ? 'image' : 'document',
            attachment_size: selectedFile.size
          };

          // Add file info to message if no text
          if (!messageText.trim()) {
            messageText = `📎 ${selectedFile.name}`;
          }
        } catch (error) {
          console.error('Error processing file:', error);
          alert('Failed to process file attachment');
          setSending(false);
          return;
        }
      }

      // Encrypt the message text
      const encryptedMessage = encryptionService.encryptMessage(messageText, conversationKey);
      
      const response = await axios.post(
        `${API}/conversations/${selectedConversation.id}/messages`,
        null,
        {
          params: {
            message_content: encryptedMessage,
            message_type: selectedFile ? 'attachment' : 'text',
            ...attachmentParams
          },
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Encrypted message sent successfully');
      
      // Add decrypted message to local state for immediate display
      const decryptedMessage = {
        ...response.data,
        message: messageText, // Show original message locally
        isDecrypted: true,
        attachment_filename: selectedFile?.name,
        attachment_type: attachmentParams.attachment_type
      };
      
      setMessages(prev => [...prev, decryptedMessage]);
      setNewMessage('');
      removeFile(); // Clear file selection
      
      // Update conversation last message in local state
      const previewMessage = messageText.length > 50 ? messageText.substring(0, 50) + '...' : messageText;
      setConversations(prev => 
        prev.map(conv => 
          conv.id === selectedConversation.id 
            ? { 
                ...conv, 
                last_message: previewMessage,
                last_message_at: new Date().toISOString()
              }
            : conv
        )
      );
    } catch (error) {
      console.error('Error sending encrypted message:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getParticipantName = (conversation) => {
    if (!conversation.participant_details || conversation.participant_details.length === 0) {
      return 'Unknown User';
    }
    return conversation.participant_details[0].name;
  };

  const getParticipantImage = (conversation) => {
    if (!conversation.participant_details || conversation.participant_details.length === 0) {
      return null;
    }
    return conversation.participant_details[0].picture;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="bg-white rounded-lg shadow h-96"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('messages')}</h1>
        
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="flex h-96">
            {/* Conversations List */}
            <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">{t('inbox')}</h2>
              </div>
              
              {conversations.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="text-4xl mb-3">💬</div>
                  <p className="text-gray-500">{t('noMessages')}</p>
                  <p className="text-sm text-gray-400 mt-1">{t('startConversation')}</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {conversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      onClick={() => setSelectedConversation(conversation)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedConversation?.id === conversation.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                      }`}
                    >
                      <div className={`flex items-center gap-3`}>
                        <div className="flex-shrink-0">
                          {getParticipantImage(conversation) ? (
                            <img
                              src={getParticipantImage(conversation)}
                              alt={getParticipantName(conversation)}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                              <span className="text-gray-600 font-medium">
                                {getParticipantName(conversation).charAt(0)}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className={`flex items-center justify-between`}>
                            <h3 className="font-medium text-gray-900 truncate">
                              {getParticipantName(conversation)}
                            </h3>
                            {conversation.last_message_at && (
                              <span className="text-xs text-gray-500">
                                {formatMessageTime(conversation.last_message_at)}
                              </span>
                            )}
                          </div>
                          {conversation.last_message && (
                            <p className="text-sm text-gray-600 truncate mt-1">
                              {conversation.last_message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Messages Area */}
            <div className="flex-1 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className={`flex items-center gap-3`}>
                      {getParticipantImage(selectedConversation) ? (
                        <img
                          src={getParticipantImage(selectedConversation)}
                          alt={getParticipantName(selectedConversation)}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 text-sm font-medium">
                            {getParticipantName(selectedConversation).charAt(0)}
                          </span>
                        </div>
                      )}
                      <h3 className="font-semibold text-gray-900 flex items-center">
                        {getParticipantName(selectedConversation)}
                        <span 
                          className="ml-2 text-green-600 text-sm" 
                          title="End-to-end encrypted conversation"
                        >
                          🔒 Encrypted
                        </span>
                      </h3>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.sender_id === user.id 
                            ? isRTL ? 'justify-start' : 'justify-end'
                            : isRTL ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                            message.sender_id === user.id
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-200 text-gray-900'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm">{message.message}</p>
                              {message.attachment_filename && (
                                <div className="mt-2 p-2 border border-current border-opacity-20 rounded">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs">
                                      {message.attachment_type === 'image' ? '🖼️' : '📄'}
                                    </span>
                                    <span className="text-xs truncate">
                                      {message.attachment_filename}
                                    </span>
                                  </div>
                                  {message.attachment_type === 'image' && message.attachment_data && (
                                    <div className="mt-1">
                                      <div className="text-xs opacity-75">
                                        🔒 Encrypted image attachment
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {message.isDecrypted && (
                              <span 
                                className={`ml-2 text-xs ${
                                  message.sender_id === user.id ? 'text-blue-200' : 'text-gray-400'
                                }`}
                                title="This message is end-to-end encrypted"
                              >
                                🔒
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <p className={`text-xs ${
                              message.sender_id === user.id ? 'text-blue-100' : 'text-gray-500'
                            }`}>
                              {formatMessageTime(message.created_at)}
                            </p>
                            {!message.isDecrypted && (
                              <span className="text-xs text-red-400" title="Decryption failed">
                                ⚠️
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-gray-200">
                    {/* File Preview */}
                    {selectedFile && (
                      <div className="mb-3 p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">
                              {selectedFile.type.startsWith('image/') ? '🖼️' : '📄'}
                            </span>
                            <div>
                              <p className="text-sm font-medium">{selectedFile.name}</p>
                              <p className="text-xs text-gray-500">
                                {(selectedFile.size / 1024).toFixed(1)} KB • Will be encrypted
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={removeFile}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            ✕
                          </button>
                        </div>
                        {filePreview && (
                          <div className="mt-2">
                            <img 
                              src={filePreview} 
                              alt="Preview" 
                              className="max-w-xs max-h-32 object-contain rounded"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className={`flex gap-2 items-end`}>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/*,.pdf,.txt"
                        className="hidden"
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer bg-gray-100 hover:bg-gray-200 p-2 rounded-lg transition-colors"
                        title="Attach file (encrypted)"
                      >
                        📎
                      </label>
                      
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={selectedFile ? "Add a message (optional)" : t('typeMessage')}
                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={sending}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={(!newMessage.trim() && !selectedFile) || sending}
                        className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {sending ? '...' : (selectedFile ? '📎 Send' : t('sendMessage'))}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl mb-4">💬</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Select a conversation
                    </h3>
                    <p className="text-gray-600">
                      Choose a conversation from the sidebar to start messaging
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;